import { useEffect } from "react";
import { SITE_DOMAIN, BRAND } from "./seoConfig";
import { LANGS } from "./i18n";

/**
 * Direct DOM-based SEO head manager. Avoids react-helmet React 18 issues.
 * On every render: updates title, meta tags, canonical, JSON-LD scripts
 * by removing previous data-managed entries and writing fresh ones.
 *
 * Usage identical to old <SEO> component — just renders nothing.
 */
const ATTR = "data-seo-managed";

function setOrCreateMeta({ name, property, content }) {
  if (!content) return;
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    if (name) el.setAttribute("name", name);
    if (property) el.setAttribute("property", property);
    el.setAttribute(ATTR, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setOrCreateLink({ rel, href }) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute(ATTR, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function clearJsonLd() {
  document.head
    .querySelectorAll(`script[type="application/ld+json"][${ATTR}="true"]`)
    .forEach((s) => s.remove());
}

function addJsonLd(schema) {
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.setAttribute(ATTR, "true");
  s.textContent = JSON.stringify(schema);
  document.head.appendChild(s);
}

function clearHreflang() {
  document.head
    .querySelectorAll(`link[rel="alternate"][hreflang][${ATTR}="true"]`)
    .forEach((l) => l.remove());
}

function setHreflang(hreflang, href) {
  const link = document.createElement("link");
  link.setAttribute("rel", "alternate");
  link.setAttribute("hreflang", hreflang);
  link.setAttribute("href", href);
  link.setAttribute(ATTR, "true");
  document.head.appendChild(link);
}

export default function SEO({
  title,
  description = BRAND.defaultDescription,
  path = "/",
  image = `${SITE_DOMAIN}/assets/logo.png`,
  schemas = [],
  noIndex = false,
  ogType = "website",
  /** When true, emit hreflang alternates for all supported languages on this path. */
  multilingual = false,
}) {
  useEffect(() => {
    const fullTitle = title
      ? `${title} · ${BRAND.name}`
      : `${BRAND.name} — ${BRAND.tagline}`;
    const url = `${SITE_DOMAIN}${path}`;

    document.title = fullTitle;

    // ── <html lang> sync for accessibility + Lighthouse SEO ──
    if (multilingual) {
      // Derive lang from the path
      const seg = (path || "/").split("/").filter(Boolean)[0];
      const known = ["hi", "mr", "gu", "ta"];
      document.documentElement.lang = known.includes(seg) ? seg : "en";
    } else {
      document.documentElement.lang = "en";
    }

    setOrCreateMeta({ name: "description", content: description });
    setOrCreateMeta({
      name: "robots",
      content: noIndex ? "noindex,nofollow" : "index,follow,max-image-preview:large",
    });

    setOrCreateLink({ rel: "canonical", href: url });

    setOrCreateMeta({ property: "og:type", content: ogType });
    setOrCreateMeta({ property: "og:site_name", content: BRAND.name });
    setOrCreateMeta({ property: "og:title", content: fullTitle });
    setOrCreateMeta({ property: "og:description", content: description });
    setOrCreateMeta({ property: "og:url", content: url });
    setOrCreateMeta({ property: "og:image", content: image });
    setOrCreateMeta({ property: "og:locale", content: "en_IN" });

    setOrCreateMeta({ name: "twitter:card", content: "summary_large_image" });
    setOrCreateMeta({ name: "twitter:site", content: BRAND.twitter });
    setOrCreateMeta({ name: "twitter:title", content: fullTitle });
    setOrCreateMeta({ name: "twitter:description", content: description });
    setOrCreateMeta({ name: "twitter:image", content: image });

    setOrCreateMeta({ name: "geo.region", content: "IN" });
    setOrCreateMeta({ name: "geo.placename", content: "India" });

    // JSON-LD: clear previous schemas before adding new ones (handles route changes)
    clearJsonLd();
    schemas.forEach(addJsonLd);

    // hreflang alternates — only emitted if `multilingual` is on
    clearHreflang();
    if (multilingual) {
      // x-default → English root
      setHreflang("x-default", `${SITE_DOMAIN}${path}`);
      LANGS.forEach((l) => {
        const langPath = l.code === "en" ? path : (path === "/" ? `/${l.code}` : `/${l.code}${path}`);
        setHreflang(l.code === "en" ? "en" : l.code, `${SITE_DOMAIN}${langPath}`);
      });
    }
  }, [title, description, path, image, JSON.stringify(schemas), noIndex, ogType, multilingual]);

  return null;
}
